/**
 * Display helpers for Agent Evolution Time-Series (Spec 2.4). Pure functions — tested.
 */

import { EvolutionPoint } from '../types';

export interface SeriesPoint {
  date: string;
  value: number | null;
}

/** Extract a single metric as a {date, value} series, skipping points missing it. */
export const extractSeries = (
  points: EvolutionPoint[] | undefined | null,
  metric: string,
): SeriesPoint[] => {
  if (!points) return [];
  return points.map((p) => ({
    date: p.date,
    value: typeof p[metric] === 'number' ? (p[metric] as number) : null,
  }));
};

/** Human label for a snapshot metric key. */
export const METRIC_LABELS: Record<string, string> = {
  rolling_win_rate: 'Rolling Win Rate',
  rolling_sharpe: 'Rolling Sharpe',
  avg_confidence: 'Avg Confidence',
  skip_rate: 'Skip Rate',
  capital_utilization_pct: 'Capital Utilization',
};

export const metricLabel = (metric: string): string =>
  METRIC_LABELS[metric] || metric.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** Net change (last − first) of a metric across the series, or null if <2 numeric points. */
export const metricTrend = (points: EvolutionPoint[] | undefined | null, metric: string): number | null => {
  const vals = extractSeries(points, metric).map((p) => p.value).filter((v): v is number => v !== null);
  if (vals.length < 2) return null;
  return Math.round((vals[vals.length - 1] - vals[0]) * 100) / 100;
};

export const hasEvolutionData = (e: { data_points?: EvolutionPoint[] } | null | undefined): boolean =>
  !!e && Array.isArray(e.data_points) && e.data_points.length > 0;
